/**
 * IRS Single Life Expectancy Table — 2022 update.
 *
 * **Source**: Treas Reg § 1.401(a)(9)-9 Q&A 1 (Single Life Table),
 * effective Jan 1, 2022 + IRS Notice 2022-6 (which approves this
 * table for SEPP/72(t) calculations under all three methods).
 * Retrieved 2026-05-08 from
 * https://www.irs.gov/publications/p590b#en_US_2024_publink100090399
 *
 * **Use**: SEPP / Rule 72(t) distribution calculations + IRA RMDs
 * post-72/73. The table maps age → remaining life expectancy in
 * years.
 *
 * Hearth v1 ships ages 30-100 — covers the realistic SEPP-eligible
 * range (typically age 50-59½ since SEPP must continue 5 years OR until
 * 59½, whichever is later).
 *
 * **Why hardcode**: 70 rows × 2 columns is a 1KB-ish dataset. Live IRS
 * lookup would require a JSON publication that doesn't exist; the
 * table changes ~once per decade. Phase 11 may add a "table version"
 * selector for older years.
 *
 * **Self-contained types** — file mirrors verbatim from
 * `shared/src/data/` to `web/src/lib/data/`.
 */

export interface SingleLifeExpectancyRow {
  readonly age: number;
  readonly years_remaining: number;
}

/** IRS Single Life Expectancy Table (2022). One row per integer age. */
export const IRS_SINGLE_LIFE_EXPECTANCY_2022: readonly SingleLifeExpectancyRow[] = [
  { age: 30, years_remaining: 55.3 },
  { age: 31, years_remaining: 54.4 },
  { age: 32, years_remaining: 53.4 },
  { age: 33, years_remaining: 52.5 },
  { age: 34, years_remaining: 51.5 },
  { age: 35, years_remaining: 50.5 },
  { age: 36, years_remaining: 49.6 },
  { age: 37, years_remaining: 48.6 },
  { age: 38, years_remaining: 47.7 },
  { age: 39, years_remaining: 46.7 },
  { age: 40, years_remaining: 45.7 },
  { age: 41, years_remaining: 44.8 },
  { age: 42, years_remaining: 43.8 },
  { age: 43, years_remaining: 42.9 },
  { age: 44, years_remaining: 41.9 },
  { age: 45, years_remaining: 41.0 },
  { age: 46, years_remaining: 40.0 },
  { age: 47, years_remaining: 39.0 },
  { age: 48, years_remaining: 38.1 },
  { age: 49, years_remaining: 37.1 },
  { age: 50, years_remaining: 36.2 },
  { age: 51, years_remaining: 35.3 },
  { age: 52, years_remaining: 34.3 },
  { age: 53, years_remaining: 33.4 },
  { age: 54, years_remaining: 32.5 },
  { age: 55, years_remaining: 31.6 },
  { age: 56, years_remaining: 30.6 },
  { age: 57, years_remaining: 29.8 },
  { age: 58, years_remaining: 28.9 },
  { age: 59, years_remaining: 28.0 },
  { age: 60, years_remaining: 27.1 },
  { age: 61, years_remaining: 26.2 },
  { age: 62, years_remaining: 25.4 },
  { age: 63, years_remaining: 24.5 },
  { age: 64, years_remaining: 23.7 },
  { age: 65, years_remaining: 22.9 },
  { age: 66, years_remaining: 22.0 },
  { age: 67, years_remaining: 21.2 },
  { age: 68, years_remaining: 20.4 },
  { age: 69, years_remaining: 19.6 },
  { age: 70, years_remaining: 18.8 },
  { age: 71, years_remaining: 18.0 },
  { age: 72, years_remaining: 17.2 },
  { age: 73, years_remaining: 16.4 },
  { age: 74, years_remaining: 15.6 },
  { age: 75, years_remaining: 14.8 },
  { age: 76, years_remaining: 14.1 },
  { age: 77, years_remaining: 13.3 },
  { age: 78, years_remaining: 12.6 },
  { age: 79, years_remaining: 11.9 },
  { age: 80, years_remaining: 11.2 },
  { age: 81, years_remaining: 10.5 },
  { age: 82, years_remaining: 9.9 },
  { age: 83, years_remaining: 9.3 },
  { age: 84, years_remaining: 8.7 },
  { age: 85, years_remaining: 8.1 },
  { age: 86, years_remaining: 7.6 },
  { age: 87, years_remaining: 7.1 },
  { age: 88, years_remaining: 6.6 },
  { age: 89, years_remaining: 6.1 },
  { age: 90, years_remaining: 5.7 },
  { age: 91, years_remaining: 5.3 },
  { age: 92, years_remaining: 4.9 },
  { age: 93, years_remaining: 4.6 },
  { age: 94, years_remaining: 4.3 },
  { age: 95, years_remaining: 4.0 },
  { age: 96, years_remaining: 3.7 },
  { age: 97, years_remaining: 3.4 },
  { age: 98, years_remaining: 3.2 },
  { age: 99, years_remaining: 3.0 },
  { age: 100, years_remaining: 2.8 },
];

/**
 * Lookup helper. Throws on out-of-range ages — caller must clamp.
 */
export function singleLifeExpectancy(age: number): number {
  if (!Number.isInteger(age)) {
    throw new Error(`singleLifeExpectancy: age must be integer, got ${age}`);
  }
  const row = IRS_SINGLE_LIFE_EXPECTANCY_2022.find((r) => r.age === age);
  if (!row) {
    throw new Error(
      `singleLifeExpectancy: age ${age} out of range (Hearth ships 30-100; clamp at caller)`,
    );
  }
  return row.years_remaining;
}
